<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

import { getContratoConsumos } from 'dashboard/api/supra';

const props = defineProps({
  numeroContrato: {
    type: String,
    required: true,
  },
  explotacion: {
    type: String,
    required: true,
  },
});

useI18n();

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// State
const consumos = ref([]);
const isLoading = ref(true);
const error = ref(null);
const selectedYear = ref('todos');
const availableYears = ref([]);

// Computed
const explotacionFormatted = computed(() => {
  return props.explotacion.padStart(2, '0');
});

const consumosFiltrados = computed(() => {
  if (selectedYear.value === 'todos') {
    return consumos.value;
  }
  return consumos.value.filter(c => c.año === selectedYear.value);
});

const stats = computed(() => {
  const filtered = consumosFiltrados.value;
  const consumosValores = filtered
    .map(c => c.metrosCubicos || 0)
    .filter(c => c > 0);

  return {
    promedio:
      consumosValores.length > 0
        ? Math.round(
            consumosValores.reduce((sum, c) => sum + c, 0) /
              consumosValores.length
          )
        : 0,
    maximo: consumosValores.length > 0 ? Math.max(...consumosValores) : 0,
    minimo: consumosValores.length > 0 ? Math.min(...consumosValores) : 0,
    totalConsumo: consumosValores.reduce((sum, c) => sum + c, 0),
  };
});

const formatM3 = value => `${value} m³`;

// Datos para el gráfico - últimos 12 períodos
const chartData = computed(() => {
  const filtered = [...consumosFiltrados.value]
    .sort((a, b) => {
      if (a.año !== b.año) {
        return parseInt(b.año, 10) - parseInt(a.año, 10);
      }
      return b.periodo.localeCompare(a.periodo);
    })
    .reverse()
    .slice(-12);

  const labels = filtered.map(c => {
    const periodo = c.periodo || '';
    const año = c.año || '';
    // periodo is text like "FEB - FEB" from the API; extract the month name
    const month = periodo.split(' ')[0] || periodo;
    if (month && año) {
      return `${month} ${año.slice(-2)}`;
    }
    return month || periodo;
  });

  return {
    labels,
    datasets: [
      {
        label: 'Consumo (m³)',
        data: filtered.map(c => c.metrosCubicos || 0),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        usePointStyle: true,
        padding: 15,
        font: { family: 'Inter, sans-serif', size: 12 },
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 12,
      titleFont: { size: 14, weight: 'bold' },
      bodyFont: { size: 12 },
      callbacks: {
        label: context => `${context.dataset.label}: ${context.parsed.y} m³`,
      },
    },
  },
  scales: {
    x: {
      display: true,
      grid: { display: true, drawBorder: false, color: 'rgba(0, 0, 0, 0.05)' },
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        font: { family: 'Inter, sans-serif', size: 11 },
        color: '#64748b',
      },
    },
    y: {
      display: true,
      beginAtZero: true,
      grid: { display: true, drawBorder: false, color: 'rgba(0, 0, 0, 0.05)' },
      ticks: {
        font: { family: 'Inter, sans-serif', size: 11 },
        color: '#64748b',
        callback: value => `${value} m³`,
      },
      title: {
        display: true,
        text: 'm³',
        font: { family: 'Inter, sans-serif', size: 12, weight: 'bold' },
        color: '#64748b',
      },
    },
  },
}));

// Methods
const fetchConsumos = async () => {
  isLoading.value = true;
  error.value = null;

  try {
    const res = await getContratoConsumos(props.numeroContrato, props.explotacion);
    const consumosRaw = res.data?.data?.consumos;

    if (!consumosRaw?.length) {
      throw new Error('No se encontraron consumos para este contrato');
    }

    const MESES_SHORT = {
      Enero: 'ENE', Febrero: 'FEB', Marzo: 'MAR', Abril: 'ABR',
      Mayo: 'MAY', Junio: 'JUN', Julio: 'JUL', Agosto: 'AGO',
      Septiembre: 'SEP', Octubre: 'OCT', Noviembre: 'NOV', Diciembre: 'DIC',
    };

    const consumosTransformados = consumosRaw
      .map(c => {
        const [mesNombre, año] = c.periodo.split(' ');
        return {
          año: año || '',
          periodo: MESES_SHORT[mesNombre] || mesNombre.slice(0, 3).toUpperCase(),
          fechaInicio: c.fechaLectura || '',
          fechaFin: c.fechaLectura || '',
          metrosCubicos: Math.round(c.consumoM3 || 0),
          estimado: c.tipoLectura === 'estimada',
          fechaLectura: c.fechaLectura || '',
        };
      })
      .sort((a, b) => {
        if (a.año !== b.año) return parseInt(b.año, 10) - parseInt(a.año, 10);
        return b.periodo.localeCompare(a.periodo);
      });

    consumos.value = consumosTransformados;

    const years = [...new Set(consumosTransformados.map(c => c.año))].sort(
      (a, b) => parseInt(b, 10) - parseInt(a, 10)
    );
    availableYears.value = years;
  } catch (err) {
    error.value = err.message || 'Error desconocido al cargar consumos';
    useAlert(error.value);
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchConsumos();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.CONSUMOS.LOADING') }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="flex flex-col items-center justify-center py-12 gap-4"
    >
      <span class="i-lucide-alert-circle text-6xl text-n-ruby-11" />
      <p class="text-n-slate-11">{{ error }}</p>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="consumos.length === 0"
      class="flex flex-col items-center justify-center py-12 gap-4"
    >
      <span class="i-lucide-droplet text-6xl text-n-slate-10" />
      <p class="text-n-slate-11">
        {{ $t('CONTRACTS.CONSUMOS.NOT_FOUND') }}
      </p>
    </div>

    <!-- Consumos Content -->
    <div v-else class="space-y-6">
      <!-- Filtro por año -->
      <div class="flex items-center gap-4">
        <label class="text-sm font-medium text-n-slate-12">{{
          $t('CONTRACTS.COMMON.FILTER_BY_YEAR')
        }}</label>
        <select
          v-model="selectedYear"
          class="px-3 py-2 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 min-w-[180px]"
        >
          <option value="todos">{{ $t('CONTRACTS.COMMON.ALL_YEARS') }}</option>
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
      </div>

      <!-- Estadísticas -->
      <div class="grid gap-4 md:grid-cols-4">
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.COMMON.MONTHLY_AVERAGE') }}
            </p>
            <span class="i-lucide-droplet text-n-slate-11" />
          </div>
          <p class="text-2xl font-bold text-n-slate-12">
            {{ formatM3(stats.promedio) }}
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ consumosFiltrados.length }} {{ $t('CONTRACTS.COMMON.PERIODS') }}
          </p>
        </div>
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.COMMON.MAX_CONSUMPTION') }}
            </p>
            <span class="i-lucide-trending-up text-n-ruby-11" />
          </div>
          <p class="text-2xl font-bold text-n-slate-12">
            {{ formatM3(stats.maximo) }}
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('CONTRACTS.COMMON.PEAK_RECORDED') }}
          </p>
        </div>
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.COMMON.MIN_CONSUMPTION') }}
            </p>
            <span class="i-lucide-trending-down text-green-600" />
          </div>
          <p class="text-2xl font-bold text-n-slate-12">
            {{ formatM3(stats.minimo) }}
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('CONTRACTS.COMMON.LOWEST_CONSUMPTION') }}
          </p>
        </div>
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <p class="text-xs text-n-slate-11">
              {{ $t('CONTRACTS.CONSUMOS.TOTAL_CONSUMPTION') }}
            </p>
            <span class="i-lucide-sigma text-n-slate-11" />
          </div>
          <p class="text-2xl font-bold text-n-slate-12">
            {{ formatM3(stats.totalConsumo) }}
          </p>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ $t('CONTRACTS.CONSUMOS.SUM_OF_PERIODS') }}
          </p>
        </div>
      </div>

      <!-- Gráfica -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h4
          class="text-lg font-semibold text-n-slate-12 mb-2 flex items-center gap-2"
        >
          <span class="i-lucide-trending-up" />
          {{ $t('CONTRACTS.CONSUMOS.EVOLUTION_TITLE') }}
        </h4>
        <p class="text-sm text-n-slate-11 mb-4">
          {{ $t('CONTRACTS.COMMON.LAST_12_PERIODS') }}
        </p>
        <div class="h-[300px] w-full">
          <Line :data="chartData" :options="chartOptions" />
        </div>
      </div>

      <!-- Tabla de Consumos -->
      <div class="bg-n-background border border-n-weak rounded-lg p-4">
        <h4
          class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
        >
          <span class="i-lucide-droplet" />
          {{
            $t('CONTRACTS.CONSUMOS.DETAIL_TITLE', {
              count: consumosFiltrados.length,
            })
          }}
        </h4>
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11"
                >
                  {{ $t('CONTRACTS.COMMON.PERIOD') }}
                </th>
                <th
                  class="text-left py-2 px-3 text-xs font-semibold text-n-slate-11"
                >
                  {{ $t('CONTRACTS.CONSUMOS.DATES') }}
                </th>
                <th
                  class="text-right py-2 px-3 text-xs font-semibold text-n-slate-11"
                >
                  {{ $t('CONTRACTS.CONSUMOS.CONSUMPTION_M3') }}
                </th>
                <th
                  class="text-center py-2 px-3 text-xs font-semibold text-n-slate-11"
                >
                  {{ $t('CONTRACTS.COMMON.TYPE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(consumo, index) in consumosFiltrados"
                :key="index"
                class="border-b border-n-weak hover:bg-n-alpha-1"
              >
                <td class="py-2 px-3 text-sm font-medium text-n-slate-12">
                  {{ consumo.periodo }} {{ consumo.año }}
                </td>
                <td class="py-2 px-3 text-sm text-n-slate-11">
                  <div v-if="consumo.fechaInicio">
                    {{
                      new Date(consumo.fechaInicio).toLocaleDateString('es-MX')
                    }}
                  </div>
                  <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
                  <div v-if="consumo.fechaFin" class="text-n-slate-10">
                    →
                    {{ new Date(consumo.fechaFin).toLocaleDateString('es-MX') }}
                  </div>
                </td>
                <td
                  class="py-2 px-3 text-sm text-right font-mono font-bold text-n-slate-12"
                >
                  {{ formatM3(consumo.metrosCubicos.toLocaleString()) }}
                </td>
                <td class="py-2 px-3 text-center">
                  <Badge
                    :color="consumo.estimado ? 'gray' : 'green'"
                    size="small"
                  >
                    {{
                      consumo.estimado
                        ? $t('CONTRACTS.COMMON.ESTIMATED')
                        : $t('CONTRACTS.COMMON.ACTUAL')
                    }}
                  </Badge>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
